"use client";

import React from "react";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, FileCheck, Fuel, Scale, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const specializedAreas = [{
  title: "Controles volumétricos",
  desc: "Supervisión y conciliación de los sistemas de controles volumétricos, registros de operación y reportes aplicables, conforme a las disposiciones fiscales vigentes del SAT.",
  icon: Fuel
}, {
  title: "CUMPLIMIENTO CNE, ASEA Y SAT",
  desc: "Acompañamiento en obligaciones regulatorias, fiscales y administrativas aplicables al sector gasolinero, así como en la atención de requerimientos ante la CNE, ASEA y SAT.",
  icon: Scale
}, {
  title: "Control y auditoría de inventarios",
  desc: "Conciliación diaria entre compras, recepción en tanques y lecturas de dispensarios para eliminar discrepancias de volumen.",
  icon: BarChart3
}, {
  title: "Auditoría administrativa de despacho",
  desc: "Revisión documental de proveedores, permisos, licencias, expedientes operativos y controles administrativos para fortalecer el cumplimiento y la atención de inspecciones.",
  icon: FileCheck
}];
const checklistItems = ["Dictámenes técnicos de controles volumétricos validados", "Conciliación precisa entre bitácoras y registros contables", "Atención integral ante requerimientos de la CNE, ASEA y SAT", "Protección legal corporativa para concesionarios y franquicias"];
export default function Gasolineras() {
  const shouldReduceMotion = useReducedMotion();
  const motionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="especialidad-en-estaciones-de-servicio-y-gasolineras" data-nav="light" className="bg-card text-card-foreground border-y border-border py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Briefing and Services */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={motionVariants} className="lg:col-span-7 flex flex-col justify-start">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-accent text-accent-foreground border-border text-[11px] font-bold tracking-widest uppercase rounded-sm px-3 py-1">
                Sector Especializado
              </Badge>
              <div className="h-px w-10 bg-border" />
            </div>

            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Especialidad en estaciones de servicio y gasolineras
            </h2>

            {/* Signature Gold Hairline motif */}
            <div className="mt-4 h-[2px] w-12 bg-ring" />

            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">Brindamos acompañamiento integral a estaciones de servicio y grupos gasolineros en México, en materia contable, fiscal, administrativa y regulatoria. Apoyamos en el cumplimiento de controles volumétricos y obligaciones fiscales ante el SAT, así como en obligaciones regulatorias aplicables ante la Comisión Nacional de Energía (CNE) y demás autoridades competentes.</p>

            {/* Checklist strip */}
            <div className="mt-8 rounded-sm border border-border bg-background p-5 shadow-xs">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4 font-default">
                Alcance Operativo y Normativo
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {checklistItems.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground leading-snug">
                    <CheckCircle2 className="size-4 shrink-0 text-foreground mt-0.5" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* 4 Specialized Grid Cards */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {specializedAreas.map((area, i) => {
              const IconComponent = area.icon;
              return <Card key={area.title} data-index={i} className="bg-background border-border border rounded-sm transition-[border-color,box-shadow] duration-200 hover:border-ring hover:shadow-sm">
                    <CardHeader className="p-5 pb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-sm bg-muted text-foreground border border-border">
                          <IconComponent className="size-4" />
                        </div>
                        <CardTitle className="text-base font-semibold text-foreground font-default leading-snug">
                          {area.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="p-5 pt-2">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {area.desc}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-4 pt-4 border-t border-border">
              <Button asChild size="lg" className="rounded-sm font-semibold tracking-wide">
                <Link to="/ContactPage">
                  Solicitar Asesoría para Gasolineras
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-sm font-semibold tracking-wide">
                <Link to="/OnlineConsultingPage">Hablar con un Especialista</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: High Quality Image & Institutional Dossier */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={motionVariants} className="lg:col-span-5 flex flex-col gap-6">
            {/* Architectural Service Station Image */}
            <div className="relative overflow-hidden rounded-sm border border-border bg-muted shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_modern_fuel_station.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_modern_fuel_station.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_modern_fuel_station.webp-wvc-sizes" alt="Estación de servicio y gasolinera comercial en México con moderna infraestructura" loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[440px] lg:h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-sm bg-background/95 backdrop-blur-xs border border-border text-foreground">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="size-4 text-foreground" />
                  <span className="text-xs font-bold uppercase tracking-wider font-default">
                    Garantía de Cumplimiento
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Evite contingencias de clausura, multas desproporcionadas y cancelaciones
                  de permisos ante dependencias federales.
                </p>
              </div>
            </div>

            {/* Advisory Information Card */}
            <div className="p-6 rounded-sm bg-background border border-border flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground font-default">
                  Firma Multidisciplinaria
                </span>
                <span className="text-xs font-semibold text-foreground">
                  ASV CONSULTORES
                </span>
              </div>
              <Separator className="bg-border" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                Supervisado directamente por auditores, fiscalistas y abogados corporativos
                con trayectoria en regulación energética, hidrocarburos y contabilidad fiscal
                en Los Mochis y el noroeste de México.
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <span>Estrategia · Cumplimiento · Resultados</span>
                <span className="font-semibold text-foreground">Atención nacional</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}